import React from 'react'
import AntdRadio from 'antd/es/radio'
import type { RadioRef } from 'antd/es/radio/interface'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import type { RadioGroupProps, RadioProps } from './antd-props'
import { Radio as ShadcnSeamRadio } from './Radio.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/radio` — one of the three plain form
 * controls put behind the design toggle in the choice batch (Checkbox, Radio,
 * Switch). Read `./Checkbox.tsx` first; it carries the batch's reasoning.
 *
 * A LEAF, not the `src/antd` barrel (PRO-130).
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * With the toggle off — the shipped default, and every page load of anyone
 * who has not picked "New design" under Theme —
 * each dispatcher renders antd's own component with the props and the ref it
 * was given: same element, same class names, same DOM.
 * `./__tests__/radio-seam-parity.test.tsx` is the test to keep green.
 *
 * All THREE parts dispatch together, and here that is not merely tidiness: a
 * `Radio` learns whether it is checked, whether it is disabled and whether it
 * is a pill from a React context its Group provides, and the two
 * implementations provide different ones. A half-switched seam would render a
 * group of radios that none of them believe they belong to.
 *
 * NOT REACHABLE FROM HERE: antd renders its own Radio inside `Table`'s
 * `rowSelection={{ type: 'radio' }}`, from within the package. That stays antd
 * in both modes — the same limit `./Checkbox.tsx` records.
 */
const RadioDispatcher = React.forwardRef<RadioRef, RadioProps>((props, ref) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamRadio {...props} ref={ref} />

  return <AntdRadio {...props} ref={ref} />
})

RadioDispatcher.displayName = 'Radio'

const GroupDispatcher = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <ShadcnSeamRadio.Group {...props} ref={ref} />

    return <AntdRadio.Group {...props} ref={ref} />
  }
)

GroupDispatcher.displayName = 'Radio.Group'

const ButtonDispatcher = React.forwardRef<RadioRef, RadioProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <ShadcnSeamRadio.Button {...props} ref={ref} />

    return <AntdRadio.Button {...props} ref={ref} />
  }
)

ButtonDispatcher.displayName = 'Radio.Button'

export const Radio = Object.assign(RadioDispatcher, {
  Group: GroupDispatcher,
  Button: ButtonDispatcher,
})
